/**
 * StoreFormatGroupEditor.tsx
 * 
 * Admin UI for creating and editing Store + Format Groups.
 * Used in "Pack + Manual Campaign" for simplified campaign authoring.
 * 
 * IMPORTANT: Groups are authoring-only constructs that flatten to 
 * store×format rows at lock time. Packs can be used as templates
 * for one-time copy (deep copy, no live linkage).
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    AlertCircle,
    Plus,
    Trash2,
    Search,
    X,
    ChevronDown,
    ChevronUp,
    Loader2,
    Store as StoreIcon,
    Image as FormatIcon,
    AlertTriangle,
    CheckCircle2,
    Package,
    PenLine,
    ArrowRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiFetch } from '@/lib/api';
import type { Store, MediaFormat, Pack } from '@shared/schema';
import type {
    StoreFormatGroup,
    GroupFormatEntry,
    GroupValidationResult,
    GroupValidationError,
    GroupValidationWarning,
    GroupPreviewStats,
    TotalPreviewStats,
} from '@shared/store-format-groups';

// ============================================================================
// Types
// ============================================================================

interface PackSnapshot {
    // Pack snapshots can have stores in different formats depending on how they were created
    stores?: Array<{
        storeId?: number;
        id?: number;
        storeName?: string;
        name?: string;
        store?: { id: number; name: string };
    }>;
    // Templates can also have different structures
    templates?: Array<{
        mediaFormatId?: number;
        quantity?: number;
        mediaFormatName?: string;
        mediaFormat?: { id: number; name: string };
    }>;
    tasks?: Array<{ taskId: number; taskName?: string }>;
    assignedMediaFormats?: Array<{ 
        taskId?: number; 
        taskName?: string;
        mediaFormatId?: number;
        quantity?: number;
    }>;
}

interface PackSnapshotApiResponse {
    packId?: number;
    status?: 'locked' | 'unlocked';
    version?: number;
    snapshot?: PackSnapshot;
}

interface StoreFormatGroupEditorProps {
    campaignId: number | null; // May be null in draft state
    retailerId: number;
    cycleId: number | null;
    groups: StoreFormatGroup[];
    onGroupsChange: (groups: StoreFormatGroup[]) => void;
    stores?: Store[];
    storesLoading?: boolean;
    availablePacks?: Pack[]; // Available packs for this retailer
    packSnapshotsData?: Record<number, PackSnapshotApiResponse>; // Pack snapshot API response by pack ID
    readOnly?: boolean;
    initialLabelIds?: Map<string, number | null>; // Map of groupId -> initial labelId for "Edited" indicator
}

interface ValidationResponse {
    valid: boolean;
    errors: GroupValidationError[];
    warnings: GroupValidationWarning[];
    preview: TotalPreviewStats;
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateGroupId(): string {
    return `group_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Sub-components
// ============================================================================

interface OperatorTask {
    id: number;
    task_id: number;
    task_name?: string;
    name?: string;
}

interface PopadAvailability {
    storeId: number;
    storeName: string;
    mediaFormatId: number;
    mediaFormatName: string;
    capacity: number;
    bookedPercent: number;
    remainingPercent: number;
    isAvailable: boolean;
}

interface GroupCardProps {
    group: StoreFormatGroup;
    index: number;
    allGroups: StoreFormatGroup[];
    stores: Store[];
    mediaFormats: MediaFormat[];
    operatorTasks: OperatorTask[];
    popadAvailability: Map<string, PopadAvailability>; // key: `${storeId}-${mediaFormatId}`
    storeFormatMap: Map<number, Set<number>>; // storeId -> Set of available formatIds
    errors: GroupValidationError[];
    warnings: GroupValidationWarning[];
    retailerLabels: Array<{ id: number; name: string; description?: string | null }>; // Retailer labels for dropdown
    initialLabelId?: number | null; // Initial labelId for "Edited" indicator
    onUpdate: (updated: StoreFormatGroup) => void;
    onDelete: () => void;
    onMoveFormats: (args: {
        sourceGroupId: string;
        selectedIndexes: number[];
        targetGroupId: string | null;
        newGroupName: string;
    }) => void;
    isExpanded: boolean;
    onToggleExpand: () => void;
    readOnly?: boolean;
}

function GroupCard({
    group,
    index,
    allGroups,
    stores,
    mediaFormats,
    operatorTasks,
    popadAvailability,
    storeFormatMap,
    errors,
    warnings,
    retailerLabels,
    initialLabelId,
    onUpdate,
    onDelete,
    onMoveFormats,
    isExpanded,
    onToggleExpand,
    readOnly = false,
}: GroupCardProps) {
    const [storeSearch, setStoreSearch] = useState('');
    const [selectedFormatIndexes, setSelectedFormatIndexes] = useState<Set<number>>(new Set());
    const [moveTargetGroupId, setMoveTargetGroupId] = useState<string>('__new__');
    const [newGroupName, setNewGroupName] = useState(() => `${group.groupName || `Group ${index + 1}`} split`);

    useEffect(() => {
        setSelectedFormatIndexes((prev) => {
            const next = new Set(Array.from(prev).filter((idx) => idx >= 0 && idx < group.formatEntries.length));
            return next.size === prev.size ? prev : next;
        });
    }, [group.formatEntries.length]);

    useEffect(() => {
        setNewGroupName((current) => current.trim() ? current : `${group.groupName || `Group ${index + 1}`} split`);
    }, [group.groupName, index]);

    // Check if this group has a limited-capacity (POPAd) format selected
    const selectedLimitedFormat = useMemo(() => {
        for (const entry of group.formatEntries) {
            const format = mediaFormats.find(f => f.id === entry.mediaFormatId);
            if (format && (format as any).capacityType === 'limited') {
                return { entry, format };
            }
        }
        return null;
    }, [group.formatEntries, mediaFormats]);

    const isPopadGroup = selectedLimitedFormat !== null;

    // Get store availability for the selected POPAd format
    const getStorePopadAvailability = useCallback((storeId: number): PopadAvailability | null => {
        if (!selectedLimitedFormat) return null;
        const key = `${storeId}-${selectedLimitedFormat.format.id}`;
        return popadAvailability.get(key) || null;
    }, [selectedLimitedFormat, popadAvailability]);

    const isFormatEntryLimited = useCallback((entry: GroupFormatEntry): boolean => {
        const format = mediaFormats.find((candidate) => candidate.id === entry.mediaFormatId);
        return entry.coveragePercent != null || Boolean(format && (format as any).capacityType === 'limited');
    }, [mediaFormats]);

    // Get selected format IDs for filtering
    const selectedFormatIds = useMemo(() => 
        group.formatEntries.map(e => e.mediaFormatId),
        [group.formatEntries]
    );

    // Filter stores based on format availability
    // For POPAd groups: only show stores with POPAd availability
    // For non-POPAd groups: only show stores that have ALL selected formats
    const availableStores = useMemo(() => {
        if (isPopadGroup && selectedLimitedFormat) {
            // POPAd: filter by POPAd availability
            return stores.filter(store => {
                const avail = getStorePopadAvailability(store.id);
                return avail && avail.isAvailable;
            });
        }
        
        // Non-POPAd: filter by format availability
        if (selectedFormatIds.length === 0) {
            // No formats selected yet - show all stores
            return stores;
        }
        
        // If we don't have store-format data yet, show all stores (fallback)
        if (storeFormatMap.size === 0) {
            return stores;
        }
        
        // Only show stores that have ALL selected formats
        return stores.filter(store => {
            const storeFormats = storeFormatMap.get(store.id);
            if (!storeFormats) return false;
            return selectedFormatIds.every(formatId => storeFormats.has(formatId));
        });
    }, [stores, isPopadGroup, selectedLimitedFormat, getStorePopadAvailability, selectedFormatIds, storeFormatMap]);

    // Filter stores based on search
    const filteredStores = useMemo(() => {
        if (!storeSearch.trim()) return availableStores;
        const searchLower = storeSearch.toLowerCase();
        return availableStores.filter(store =>
            store.name?.toLowerCase().includes(searchLower) ||
            store.address?.toLowerCase().includes(searchLower)
        );
    }, [availableStores, storeSearch]);

    // Create store and format lookup maps for error display
    const storeMap = useMemo(() =>
        new Map(stores.map(s => [s.id, s.name || `Store ${s.id}`])),
        [stores]
    );
    const formatMap = useMemo(() =>
        new Map(mediaFormats.map(f => [f.id, f.name || `Format ${f.id}`])),
        [mediaFormats]
    );

    // Calculate group preview stats (with POPAd unit calculation)
    const preview = useMemo((): GroupPreviewStats => {
        const storeCount = group.storeIds.length;
        const formatCount = group.formatEntries.length;
        
        // For POPAd groups, calculate actual units based on coverage and capacity
        if (isPopadGroup && selectedLimitedFormat) {
            const defaultCoverage = selectedLimitedFormat.entry.coveragePercent || 0;
            let totalUnits = 0;
            
            for (const storeId of group.storeIds) {
                const avail = popadAvailability.get(`${storeId}-${selectedLimitedFormat.format.id}`);
                if (avail) {
                    const storeCoverage = group.storeCoverageOverrides?.[storeId] ?? defaultCoverage;
                    totalUnits += Math.floor((storeCoverage / 100) * avail.capacity);
                }
            }
            
            return {
                storeCount,
                formatCount,
                assignmentCount: storeCount * formatCount,
                unitCount: totalUnits,
            };
        }
        
        // Standard calculation for non-POPAd groups
        const totalQuantity = group.formatEntries.reduce((sum, e) => sum + e.quantity, 0);
        return {
            storeCount,
            formatCount,
            assignmentCount: storeCount * formatCount,
            unitCount: storeCount * totalQuantity,
        };
    }, [group, isPopadGroup, selectedLimitedFormat, popadAvailability]);

    // Group-specific errors and warnings
    const groupErrors = errors.filter(e => e.groupId === group.groupId);
    const groupWarnings = warnings.filter(w => w.groupId === group.groupId);
    const movableTargetGroups = allGroups.filter((candidate) => candidate.groupId !== group.groupId);
    const selectedMoveIndexes = Array.from(selectedFormatIndexes).sort((a, b) => a - b);
    const selectedMoveEntries = selectedMoveIndexes
        .map((idx) => group.formatEntries[idx])
        .filter((entry): entry is GroupFormatEntry => Boolean(entry));
    const selectedMoveFormatIds = new Set(selectedMoveEntries.map((entry) => entry.mediaFormatId).filter((id) => id > 0));
    const selectedMoveIncludesLimitedFormat = selectedMoveEntries.some(isFormatEntryLimited);
    const targetGroup = moveTargetGroupId === '__new__'
        ? null
        : movableTargetGroups.find((candidate) => candidate.groupId === moveTargetGroupId) ?? null;
    const targetIsPopadGroup = Boolean(
        targetGroup && targetGroup.formatEntries.some((entry) => {
            const format = mediaFormats.find((candidate) => candidate.id === entry.mediaFormatId);
            return entry.coveragePercent != null || (format && (format as any).capacityType === 'limited');
        })
    );
    const targetHasSelectedFormat = Boolean(
        targetGroup && targetGroup.formatEntries.some((entry) => selectedMoveFormatIds.has(entry.mediaFormatId))
    );
    const isMovingAllFormats = selectedMoveIndexes.length >= group.formatEntries.length;
    const canMoveSelectedFormats =
        selectedMoveIndexes.length > 0 &&
        !isMovingAllFormats &&
        !selectedMoveIncludesLimitedFormat &&
        !targetIsPopadGroup &&
        !targetHasSelectedFormat &&
        (moveTargetGroupId !== '__new__' || newGroupName.trim().length > 0);

    // Helpers
    const toggleStore = (storeId: number) => {
        const isSelected = group.storeIds.includes(storeId);
        const newStoreIds = isSelected
            ? group.storeIds.filter(id => id !== storeId)
            : [...group.storeIds, storeId];
        onUpdate({ ...group, storeIds: newStoreIds });
    };

    const addFormat = () => {
        // Don't allow adding more formats if a POPAd format is selected
        if (isPopadGroup) return;
        onUpdate({
            ...group,
            formatEntries: [...group.formatEntries, { mediaFormatId: 0, quantity: 1 }],
        });
    };

    const updateFormat = (idx: number, field: 'mediaFormatId' | 'quantity' | 'coveragePercent', value: number) => {
        const updated = [...group.formatEntries];
        const newFormat = mediaFormats.find(f => f.id === value);
        const isNewFormatLimited = newFormat && (newFormat as any).capacityType === 'limited';
        
        // If changing to a limited format, it must be the only format and clear stores
        if (field === 'mediaFormatId' && isNewFormatLimited) {
            // Replace all formats with just this one limited format
            onUpdate({
                ...group,
                formatEntries: [{ mediaFormatId: value, quantity: 1, coveragePercent: null }],
                storeIds: [], // Clear stores - user must reselect based on availability
            });
            return;
        }
        
        updated[idx] = { ...updated[idx], [field]: value };
        onUpdate({ ...group, formatEntries: updated });
    };

    const updateFormatCoverage = (idx: number, coveragePercent: number) => {
        const updated = [...group.formatEntries];
        updated[idx] = { ...updated[idx], coveragePercent };
        // When coverage changes, clear stores that no longer have sufficient availability
        const formatId = updated[idx].mediaFormatId;
        const validStoreIds = group.storeIds.filter(storeId => {
            const key = `${storeId}-${formatId}`;
            const avail = popadAvailability.get(key);
            return avail && avail.isAvailable && avail.remainingPercent >= coveragePercent;
        });
        onUpdate({ ...group, formatEntries: updated, storeIds: validStoreIds });
    };

    const removeFormat = (idx: number) => {
        setSelectedFormatIndexes((prev) => {
            const next = new Set<number>();
            for (const selectedIdx of prev) {
                if (selectedIdx < idx) next.add(selectedIdx);
                if (selectedIdx > idx) next.add(selectedIdx - 1);
            }
            return next;
        });
        onUpdate({
            ...group,
            formatEntries: group.formatEntries.filter((_, i) => i !== idx),
        });
    };

    const toggleFormatMoveSelection = (idx: number) => {
        setSelectedFormatIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            return next;
        });
    };

    const handleMoveSelectedFormats = () => {
        if (!canMoveSelectedFormats) return;
        onMoveFormats({
            sourceGroupId: group.groupId,
            selectedIndexes: selectedMoveIndexes,
            targetGroupId: moveTargetGroupId === '__new__' ? null : moveTargetGroupId,
            newGroupName: newGroupName.trim(),
        });
        setSelectedFormatIndexes(new Set());
        setMoveTargetGroupId('__new__');
    };

    const hasErrors = groupErrors.length > 0;
    const hasWarnings = groupWarnings.length > 0;

    return (
        <Card className={`transition-all ${hasErrors ? 'border-red-300 bg-red-50/30' : hasWarnings ? 'border-yellow-300 bg-yellow-50/30' : ''}`}>
            {/* Header - always visible */}
            <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer select-none hover:bg-muted/50 transition-colors"
                onClick={onToggleExpand}
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Badge variant="outline" className="shrink-0">
                        #{index + 1}
                    </Badge>

                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {!readOnly ? (
                            <Input
                                value={group.groupName}
                                onChange={(e) => onUpdate({ ...group, groupName: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Group name (required)"
                                className={`font-medium max-w-xs ${!group.groupName.trim() ? 'border-red-300' : ''}`}
                            />
                        ) : (
                            <span className="font-medium truncate">{group.groupName || 'Unnamed Group'}</span>
                        )}
                        
                        {/* Label dropdown - optional */}
                        {!readOnly && retailerLabels.length > 0 && (
                            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                                <Select
                                    value={group.labelId ? String(group.labelId) : undefined}
                                    onValueChange={(val) => {
                                        if (val === "__none__" || !val) {
                                            onUpdate({ ...group, labelId: null });
                                        } else {
                                            const labelId = parseInt(val, 10);
                                            onUpdate({ ...group, labelId: isNaN(labelId) ? null : labelId });
                                        }
                                    }}
                                >
                                    <SelectTrigger className="w-48">
                                        <SelectValue placeholder="Label (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">No label</SelectItem>
                                        {retailerLabels.map((label) => (
                                            <SelectItem key={label.id} value={String(label.id)}>
                                                {label.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {/* Show "Edited" indicator when labelId differs from initial */}
                                {initialLabelId !== undefined && group.labelId !== initialLabelId && (
                                    <Badge variant="outline" className="shrink-0 text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        <PenLine className="h-3 w-3 mr-1" />
                                        Edited
                                    </Badge>
                                )}
                            </div>
                        )}
                        {readOnly && group.labelId && (
                            <Badge variant="outline" className="shrink-0">
                                {retailerLabels.find(l => l.id === group.labelId)?.name || 'Label'}
                            </Badge>
                        )}
                    </div>

                    {/* Quick stats */}
                    <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                        <span className="flex items-center gap-1">
                            <StoreIcon className="h-3 w-3" />
                            {preview.storeCount}
                        </span>
                        <span className="flex items-center gap-1">
                            <FormatIcon className="h-3 w-3" />
                            {preview.formatCount}
                        </span>
                        <span>= {preview.unitCount} units</span>
                    </div>

                    {/* Status indicators */}
                    {hasErrors && (
                        <Badge variant="destructive" className="shrink-0">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {groupErrors.length} error{groupErrors.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                    {hasWarnings && !hasErrors && (
                        <Badge variant="secondary" className="shrink-0 bg-yellow-100 text-yellow-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {groupWarnings.length} warning{groupWarnings.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {!readOnly && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
            </div>

            {/* Expanded content */}
            {isExpanded && (
                <CardContent className="border-t pt-4 space-y-4">
                    {/* Step 1: Media Formats */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs mr-2">1</span>
                                Select Formats
                            </Label>
                            {!readOnly && !isPopadGroup && (
                                <Button type="button" variant="outline" size="sm" onClick={addFormat}>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Format
                                </Button>
                            )}
                        </div>

                        <p className={`text-xs p-2 rounded flex items-start gap-1.5 ${isPopadGroup ? 'text-amber-600 bg-amber-50' : 'text-muted-foreground'}`}>
                            <span className="font-medium">💡</span>
                            {isPopadGroup 
                                ? 'POPAds have variable quantities per store, so they need to be in a group on their own.'
                                : 'Select the media formats for this group. Formats with variable quantities (e.g. POPAds) need their own group.'
                            }
                        </p>

                        {group.formatEntries.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">No formats added. Click "Add Format" to start.</p>
                        ) : (
                            <div className="space-y-2">
                                {group.formatEntries.map((entry, idx) => {
                                    const entryFormat = mediaFormats.find(f => f.id === entry.mediaFormatId);
                                    const isLimitedFormat = entryFormat && (entryFormat as any).capacityType === 'limited';
                                    
                                    return (
                                        <div key={idx} className={`flex items-center gap-2 p-2 border rounded-lg ${isLimitedFormat ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                                            {!readOnly && group.formatEntries.length > 1 && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFormatIndexes.has(idx)}
                                                    onChange={() => toggleFormatMoveSelection(idx)}
                                                    className="h-4 w-4"
                                                    aria-label={`Select format ${idx + 1} to move`}
                                                    disabled={Boolean(isLimitedFormat)}
                                                    title={isLimitedFormat ? 'Limited-capacity formats must stay in their own group' : 'Select this format to move'}
                                                />
                                            )}
                                            <Select
                                                value={entry.mediaFormatId > 0 ? entry.mediaFormatId.toString() : undefined}
                                                onValueChange={(val) => updateFormat(idx, 'mediaFormatId', parseInt(val, 10))}
                                                disabled={readOnly}
                                            >
                                                <SelectTrigger className="flex-1">
                                                    <SelectValue placeholder="Select media format" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {mediaFormats.map((mf) => (
                                                        <SelectItem key={mf.id} value={mf.id.toString()}>
                                                            {mf.name}
                                                            {(mf as any).capacityType === 'limited' && (
                                                                <span className="ml-1 text-xs text-amber-600">(Limited Capacity)</span>
                                                            )}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>

                                            {/* For limited formats: show coverage selector instead of quantity */}
                                            {isLimitedFormat ? (
                                                <div className="flex items-center gap-1">
                                                    <Label className="text-xs text-amber-700">Coverage:</Label>
                                                    <Select
                                                        value={entry.coveragePercent?.toString() || ""}
                                                        onValueChange={(val) => updateFormatCoverage(idx, parseInt(val, 10))}
                                                        disabled={readOnly}
                                                    >
                                                        <SelectTrigger className="w-24">
                                                            <SelectValue placeholder="Select %" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {[25, 50, 75, 100].map(pct => (
                                                                <SelectItem key={pct} value={pct.toString()}>
                                                                    {pct}%
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1">
                                                    <Label className="text-xs">Qty:</Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={entry.quantity}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value, 10);
                                                            updateFormat(idx, 'quantity', isNaN(val) ? 1 : Math.max(1, val));
                                                        }}
                                                        className="w-16 text-center"
                                                        disabled={readOnly}
                                                    />
                                                </div>
                                            )}

                                            {!readOnly && !isPopadGroup && group.formatEntries.length > 1 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeFormat(idx)}
                                                    className="text-red-600 hover:text-red-700"
                                                    title="Remove format"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {!readOnly && group.formatEntries.length > 1 && (
                            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
                                    <div className="flex-1 space-y-1">
                                        <Label className="text-xs font-medium">Move selected formats to</Label>
                                        <Select value={moveTargetGroupId} onValueChange={setMoveTargetGroupId}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Choose group" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__new__">New group</SelectItem>
                                                {movableTargetGroups.map((candidate) => (
                                                    <SelectItem key={candidate.groupId} value={candidate.groupId}>
                                                        {candidate.groupName || 'Unnamed Group'}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {moveTargetGroupId === '__new__' && (
                                        <div className="flex-1 space-y-1">
                                            <Label className="text-xs font-medium">New group name</Label>
                                            <Input
                                                value={newGroupName}
                                                onChange={(event) => setNewGroupName(event.target.value)}
                                                placeholder="New group name"
                                            />
                                        </div>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleMoveSelectedFormats}
                                        disabled={!canMoveSelectedFormats}
                                    >
                                        <ArrowRight className="h-4 w-4 mr-1" />
                                        Move selected
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {selectedMoveIndexes.length === 0
                                        ? 'Select one or more formats above. The source group must keep at least one format.'
                                        : isMovingAllFormats
                                            ? 'Leave at least one format in the source group. Delete the group separately if it is no longer needed.'
                                            : selectedMoveIncludesLimitedFormat
                                                ? 'Limited-capacity formats must stay in their own group. Move the other formats out instead.'
                                            : targetIsPopadGroup
                                                ? 'POPAd groups must stay on their own, so move these formats to a different group.'
                                            : targetHasSelectedFormat
                                                ? 'The target group already contains one of the selected formats.'
                                                : moveTargetGroupId === '__new__'
                                                    ? 'New groups copy this group’s stores and operator task by default.'
                                                    : 'Selected formats will move into the chosen group and use that group’s stores.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Step 2: Stores */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs mr-2">2</span>
                                Select Stores ({group.storeIds.length} selected)
                            </Label>
                            <div className="flex items-center gap-2">
                                {!readOnly && (
                                    <>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (isPopadGroup && selectedLimitedFormat?.entry.coveragePercent) {
                                                    // Select all eligible stores for POPAd
                                                    const eligibleStoreIds = filteredStores
                                                        .filter(store => {
                                                            const avail = getStorePopadAvailability(store.id);
                                                            const requiredCoverage = selectedLimitedFormat.entry.coveragePercent || 0;
                                                            return avail && avail.remainingPercent >= requiredCoverage;
                                                        })
                                                        .map(s => s.id);
                                                    onUpdate({ ...group, storeIds: eligibleStoreIds });
                                                } else if (!isPopadGroup) {
                                                    // Select all filtered stores for non-POPAd
                                                    const allStoreIds = filteredStores.map(s => s.id);
                                                    onUpdate({ ...group, storeIds: allStoreIds });
                                                }
                                            }}
                                            disabled={isPopadGroup && !selectedLimitedFormat?.entry.coveragePercent}
                                        >
                                            Select All
                                        </Button>
                                        {group.storeIds.length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onUpdate({ ...group, storeIds: [] })}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </>
                                )}
                                {isPopadGroup && (
                                    <Badge variant="secondary" className="text-xs">
                                        {filteredStores.length} available
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {isPopadGroup ? (
                            !selectedLimitedFormat?.entry.coveragePercent ? (
                                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded flex items-start gap-1.5">
                                    <span className="font-medium">💡</span>
                                    Select a coverage percentage above to see available stores.
                                </p>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    💡 Only stores with sufficient POPAd capacity are shown below.
                                </p>
                            )
                        ) : selectedFormatIds.length > 0 ? (
                            <p className="text-xs text-muted-foreground">
                                💡 Only stores that have all {selectedFormatIds.length} selected format{selectedFormatIds.length > 1 ? 's' : ''} are shown below.
                            </p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                💡 Select formats above first, then choose which stores to include.
                            </p>
                        )}

                        {/* Store search - always shown */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search stores..."
                                value={storeSearch}
                                onChange={(e) => setStoreSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setStoreSearch('');
                                        e.currentTarget.blur();
                                    }
                                }}
                                className="pl-9 pr-9"
                            />
                            {storeSearch && (
                                <button
                                    type="button"
                                    onClick={() => setStoreSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        {isPopadGroup && selectedLimitedFormat?.entry.coveragePercent ? (
                            /* POPAd Tile-based Store Selection */
                            <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">
                                    Stores with available capacity for {selectedLimitedFormat.format.name}
                                </p>
                                
                                {filteredStores.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-muted-foreground border rounded-lg bg-gray-50">
                                        {storeSearch 
                                            ? `No stores match "${storeSearch}"`
                                            : 'No stores with sufficient capacity for this coverage level'
                                        }
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto p-1">
                                        {filteredStores.map((store) => {
                                            const avail = getStorePopadAvailability(store.id);
                                            const isSelected = group.storeIds.includes(store.id);
                                            const defaultCoverage = selectedLimitedFormat.entry.coveragePercent || 0;
                                            const storeCoverage = group.storeCoverageOverrides?.[store.id] ?? defaultCoverage;
                                            const isEligible = avail && avail.remainingPercent >= defaultCoverage;
                                            
                                            // Calculate planned units for this store
                                            const plannedUnits = avail ? Math.floor((storeCoverage / 100) * avail.capacity) : 0;
                                            
                                            return (
                                                <Card 
                                                    key={store.id} 
                                                    className={`transition-all ${
                                                        isSelected 
                                                            ? 'ring-2 ring-primary bg-primary/5' 
                                                            : isEligible 
                                                                ? 'hover:bg-muted cursor-pointer' 
                                                                : 'opacity-50'
                                                    }`}
                                                >
                                                    <CardContent className="p-3">
                                                        <div className="flex items-start gap-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleStore(store.id)}
                                                                className="h-4 w-4 mt-0.5 cursor-pointer"
                                                                disabled={readOnly || !isEligible}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <h4 
                                                                    className="font-medium text-sm truncate cursor-pointer"
                                                                    onClick={() => !readOnly && isEligible && toggleStore(store.id)}
                                                                >
                                                                    {store.name}
                                                                </h4>
                                                                {store.address && (
                                                                    <p className="text-xs text-muted-foreground truncate">
                                                                        {store.address}
                                                                    </p>
                                                                )}
                                                                
                                                                {/* Availability info */}
                                                                {avail && (
                                                                    <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <span className="text-xs text-amber-700 font-medium">
                                                                                {selectedLimitedFormat.format.name}
                                                                            </span>
                                                                            <Badge 
                                                                                variant="default"
                                                                                className="text-xs bg-amber-500"
                                                                            >
                                                                                {avail.remainingPercent}% avail ({avail.capacity} units)
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        {/* Per-store coverage selector */}
                                                                        {isSelected && (
                                                                            <div className="flex items-center gap-1 flex-wrap">
                                                                                {[25, 50, 75, 100].map(pct => {
                                                                                    const canSelect = avail.remainingPercent >= pct;
                                                                                    const isActive = storeCoverage === pct;
                                                                                    return (
                                                                                        <button
                                                                                            key={pct}
                                                                                            type="button"
                                                                                            disabled={readOnly || !canSelect}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                const newOverrides = { ...(group.storeCoverageOverrides || {}) };
                                                                                                if (pct === defaultCoverage) {
                                                                                                    // Remove override if setting to default
                                                                                                    delete newOverrides[store.id];
                                                                                                } else {
                                                                                                    newOverrides[store.id] = pct;
                                                                                                }
                                                                                                onUpdate({ ...group, storeCoverageOverrides: newOverrides });
                                                                                            }}
                                                                                            className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                                                                                                isActive 
                                                                                                    ? 'bg-amber-600 text-white border-amber-600' 
                                                                                                    : canSelect
                                                                                                        ? 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'
                                                                                                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                                            }`}
                                                                                        >
                                                                                            {pct}%
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                                <span className="text-xs text-amber-800 ml-1">
                                                                                    = {plannedUnits} units
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Standard checkbox list for unlimited formats */
                            <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                                {stores.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                        No stores available
                                    </div>
                                ) : selectedFormatIds.length > 0 && availableStores.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                        <p>No stores have all selected formats</p>
                                        <p className="text-xs mt-1">
                                            {selectedFormatIds.length === 1 
                                                ? 'No stores have this format assigned'
                                                : `No stores have all ${selectedFormatIds.length} formats assigned`
                                            }
                                        </p>
                                    </div>
                                ) : filteredStores.length === 0 && storeSearch ? (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                        No stores match "{storeSearch}"
                                    </div>
                                ) : filteredStores.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                        {selectedFormatIds.length === 0 
                                            ? 'Select a format first to see available stores'
                                            : 'No stores available'
                                        }
                                    </div>
                                ) : (
                                    <div className="divide-y">
                                        {filteredStores.map((store) => (
                                            <label
                                                key={store.id}
                                                className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                                            >
                                                <span className="flex-1 pr-2 truncate">
                                                    <span className="font-medium">{store.name}</span>
                                                    {store.address && (
                                                        <span className="ml-2 text-muted-foreground text-xs">
                                                            {store.address}
                                                        </span>
                                                    )}
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    checked={group.storeIds.includes(store.id)}
                                                    onChange={() => toggleStore(store.id)}
                                                    className="h-4 w-4"
                                                    disabled={readOnly}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {(storeSearch || (selectedFormatIds.length > 0 && availableStores.length < stores.length)) && (
                            <p className="text-xs text-muted-foreground">
                                {storeSearch 
                                    ? `Showing ${filteredStores.length} of ${availableStores.length} stores`
                                    : `${availableStores.length} of ${stores.length} stores have the selected format${selectedFormatIds.length > 1 ? 's' : ''}`
                                }
                            </p>
                        )}
                    </div>

                    {/* Step 3: Operator Task (Payment Rate) - for all groups including POPAd */}
                    <div className="space-y-2">
                        <Label className="text-sm font-semibold">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs mr-2">3</span>
                            Operator Task <span className="text-destructive">*</span>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            Select the operator task that determines payment rates for this group
                        </p>
                        <Select
                            value={group.operatorTaskId ? String(group.operatorTaskId) : undefined}
                            onValueChange={(val) => {
                                const parsed = parseInt(val, 10);
                                onUpdate({
                                    ...group,
                                    operatorTaskId: isNaN(parsed) ? null : parsed,
                                });
                            }}
                            disabled={readOnly || operatorTasks.length === 0}
                        >
                            <SelectTrigger className={!group.operatorTaskId ? 'border-destructive' : ''}>
                                <SelectValue placeholder={operatorTasks.length === 0 ? 'No operator tasks available' : 'Select operator task...'} />
                            </SelectTrigger>
                            <SelectContent>
                                {operatorTasks.map((task) => (
                                    <SelectItem key={task.id} value={String(task.task_id)}>
                                        {task.task_name || task.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Preview Stats */}
                    <div className={`rounded-lg p-3 ${isPopadGroup ? 'bg-amber-50 border border-amber-200' : 'bg-muted/50'}`}>
                        <p className="text-sm font-semibold mb-2">
                            {isPopadGroup ? 'POPAd Summary' : 'Preview'}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                            <div>
                                <p className="text-muted-foreground">Stores</p>
                                <p className="font-medium">{preview.storeCount}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">
                                    {isPopadGroup ? 'Format' : 'Formats'}
                                </p>
                                <p className="font-medium">
                                    {isPopadGroup && selectedLimitedFormat 
                                        ? selectedLimitedFormat.format.name 
                                        : preview.formatCount
                                    }
                                </p>
                            </div>
                            {isPopadGroup && selectedLimitedFormat?.entry.coveragePercent && (
                                <div>
                                    <p className="text-muted-foreground">Default Coverage</p>
                                    <p className="font-medium">{selectedLimitedFormat.entry.coveragePercent}%</p>
                                </div>
                            )}
                            <div>
                                <p className="text-muted-foreground">
                                    {isPopadGroup ? 'Total Trolleys' : 'Total Units'}
                                </p>
                                <p className="font-medium text-lg">{preview.unitCount.toLocaleString()}</p>
                            </div>
                        </div>
                        {isPopadGroup && group.storeCoverageOverrides && Object.keys(group.storeCoverageOverrides).length > 0 && (
                            <p className="text-xs text-amber-700 mt-2">
                                {Object.keys(group.storeCoverageOverrides).length} store(s) with custom coverage
                            </p>
                        )}
                    </div>

                    {/* Validation Errors/Warnings - at the bottom */}
                    {groupErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                            <p className="text-sm text-red-800 font-semibold flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                Validation Errors
                            </p>
                            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                                {groupErrors.map((err, i) => (
                                    <li key={i}>
                                        {err.type === 'OVERLAP' && err.storeId && err.mediaFormatId ? (
                                            <>
                                                <strong>{storeMap.get(err.storeId)}</strong> × <strong>{formatMap.get(err.mediaFormatId)}</strong>
                                                {' '}already assigned in another group
                                            </>
                                        ) : (
                                            err.message
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {groupWarnings.length > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 space-y-1">
                            <p className="text-sm text-yellow-800 font-semibold flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Warnings
                            </p>
                            <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                                {groupWarnings.map((warn, i) => (
                                    <li key={i}>
                                        {warn.storeId && warn.mediaFormatId ? (
                                            <>
                                                <strong>{storeMap.get(warn.storeId)}</strong> × <strong>{formatMap.get(warn.mediaFormatId)}</strong>: {warn.message}
                                            </>
                                        ) : (
                                            warn.message
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

// ============================================================================
// Main Component
// ============================================================================

// ============================================================================
// Group Start Mode Types
// ============================================================================

type GroupStartMode = 'scratch' | 'pack';

// ============================================================================
// Helper: Convert pack snapshot to StoreFormatGroup (deep copy)
// ============================================================================

function convertPackToGroup(
    packId: number,
    packName: string,
    snapshot: PackSnapshot,
    groupNumber: number
): StoreFormatGroup {
    console.log('[convertPackToGroup] Converting pack snapshot:', { packId, packName, snapshot });
    
    // Extract store IDs from snapshot (handle different structures)
    const storeIds: number[] = [];
    if (snapshot.stores && Array.isArray(snapshot.stores)) {
        for (const storeEntry of snapshot.stores) {
            // Try different possible structures
            const storeId = storeEntry.storeId ?? storeEntry.id ?? storeEntry.store?.id;
            if (typeof storeId === 'number' && storeId > 0) {
                storeIds.push(storeId);
            }
        }
    }
    console.log('[convertPackToGroup] Extracted storeIds:', storeIds);

    // Extract format entries from templates (handle different structures)
    const formatEntries: GroupFormatEntry[] = [];
    const seenFormatIds = new Set<number>();
    
    if (snapshot.templates && Array.isArray(snapshot.templates)) {
        for (const template of snapshot.templates) {
            // Try different possible structures
            const mediaFormatId = template.mediaFormatId ?? template.mediaFormat?.id;
            const quantity = template.quantity ?? 1;
            
            if (typeof mediaFormatId === 'number' && mediaFormatId > 0 && !seenFormatIds.has(mediaFormatId)) {
                seenFormatIds.add(mediaFormatId);
                formatEntries.push({
                    mediaFormatId,
                    quantity,
                });
            }
        }
    }
    
    // If no templates, try to extract from assignedMediaFormats
    if (formatEntries.length === 0 && snapshot.assignedMediaFormats && Array.isArray(snapshot.assignedMediaFormats)) {
        for (const amf of snapshot.assignedMediaFormats) {
            const mediaFormatId = amf.mediaFormatId;
            const quantity = amf.quantity ?? 1;
            
            if (typeof mediaFormatId === 'number' && mediaFormatId > 0 && !seenFormatIds.has(mediaFormatId)) {
                seenFormatIds.add(mediaFormatId);
                formatEntries.push({
                    mediaFormatId,
                    quantity,
                });
            }
        }
    }
    console.log('[convertPackToGroup] Extracted formatEntries:', formatEntries);

    // Extract operator task ID from tasks or assignedMediaFormats
    let operatorTaskId: number | null = null;
    if (snapshot.tasks && Array.isArray(snapshot.tasks) && snapshot.tasks.length > 0) {
        operatorTaskId = snapshot.tasks[0].taskId ?? null;
    } else if (snapshot.assignedMediaFormats && Array.isArray(snapshot.assignedMediaFormats) && snapshot.assignedMediaFormats.length > 0) {
        const withTask = snapshot.assignedMediaFormats.find(a => a.taskId);
        if (withTask) {
            operatorTaskId = withTask.taskId ?? null;
        }
    }
    console.log('[convertPackToGroup] Extracted operatorTaskId:', operatorTaskId);

    const result = {
        groupId: generateGroupId(),
        groupName: `${packName} (from pack)`,
        storeIds,
        formatEntries,
        operatorTaskId,
        labelId: null, // Labels are not copied from packs - user can assign manually
        // No live linkage to pack - this is a deep copy
    };
    
    console.log('[convertPackToGroup] Final group:', result);
    return result;
}

export function StoreFormatGroupEditor({
    campaignId,
    retailerId,
    cycleId,
    groups,
    onGroupsChange,
    stores: providedStores,
    storesLoading: providedStoresLoading = false,
    availablePacks = [],
    packSnapshotsData = {},
    readOnly = false,
    initialLabelIds,
}: StoreFormatGroupEditorProps) {
    const { toast } = useToast();
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    
    // Group Start Mode dialog state
    const [showStartModeDialog, setShowStartModeDialog] = useState(false);
    const [selectedStartMode, setSelectedStartMode] = useState<GroupStartMode>('scratch');
    
    // Pack Selection dialog state
    const [showPackSelectionDialog, setShowPackSelectionDialog] = useState(false);
    const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
    const [packSearchQuery, setPackSearchQuery] = useState('');

    // Fetch stores for this retailer
    const shouldFetchStores = typeof providedStores === 'undefined';
    const { data: fetchedStores = [], isLoading: fetchedStoresLoading } = useQuery<Store[]>({
        queryKey: ['/api/retailers', retailerId, 'stores'],
        queryFn: async () => {
            const response = await apiFetch(`/api/retailers/${retailerId}/stores`);
            if (!response.ok) throw new Error('Failed to fetch stores');
            return response.json();
        },
        enabled: shouldFetchStores && !!retailerId,
    });
    const stores = providedStores ?? fetchedStores;
    const storesLoading = shouldFetchStores ? fetchedStoresLoading : providedStoresLoading;

    // Fetch media formats for this retailer
    const { data: mediaFormats = [], isLoading: formatsLoading } = useQuery<MediaFormat[]>({
        queryKey: ['/api/media-formats', retailerId],
        queryFn: async () => {
            const response = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
            if (!response.ok) throw new Error('Failed to fetch media formats');
            return response.json();
        },
        enabled: !!retailerId,
    });

    // Fetch operator tasks for payment rate selection
    const { data: operatorTasks = [], isLoading: operatorTasksLoading } = useQuery<OperatorTask[]>({
        queryKey: ['/api/operator-tasks'],
        queryFn: async () => {
            const response = await apiFetch('/api/operator-tasks');
            if (!response.ok) throw new Error('Failed to fetch operator tasks');
            return response.json();
        },
    });

    // Fetch retailer labels for label dropdown
    const { data: retailerLabels = [] } = useQuery<Array<{ id: number; name: string; description?: string | null }>>({
        queryKey: ['/api/retailers', retailerId, 'labels'],
        queryFn: async () => {
            const response = await apiFetch(`/api/retailers/${retailerId}/labels`);
            if (!response.ok) {
                // Labels are optional - don't fail if endpoint doesn't exist or returns error
                console.warn('[StoreFormatGroupEditor] Failed to fetch retailer labels:', response.status);
                return [];
            }
            return response.json();
        },
        enabled: !!retailerId,
    });

    // Fetch POPAd availability for this cycle
    const { data: popadAvailabilityData } = useQuery<{ cycleId: number; cycleName: string; availability: PopadAvailability[] }>({
        queryKey: ['/api/popad/availability', cycleId],
        queryFn: async () => {
            const response = await apiFetch(`/api/popad/availability/${cycleId}`);
            if (!response.ok) throw new Error('Failed to fetch POPAd availability');
            return response.json();
        },
        enabled: !!cycleId,
    });

    // Fetch store-media-requirements to know which formats are available at each store
    const { data: storeMediaRequirements = [] } = useQuery<Array<{ storeId: number; mediaFormatId: number; quantity: number }>>({
        queryKey: ['/api/store-media-requirements', retailerId],
        queryFn: async () => {
            console.log('[StoreFormatGroupEditor] Fetching store-media-requirements for retailerId:', retailerId);
            const response = await apiFetch(`/api/store-media-requirements?retailerId=${retailerId}`);
            if (!response.ok) {
                console.warn('[StoreFormatGroupEditor] Failed to fetch store-media-requirements:', response.status);
                return [];
            }
            const data = await response.json();
            console.log('[StoreFormatGroupEditor] Fetched store-media-requirements:', data.length, 'records');
            return data;
        },
        enabled: !!retailerId,
    });

    // Build a map: storeId -> Set of formatIds available at that store
    const storeFormatMap = useMemo(() => {
        const map = new Map<number, Set<number>>();
        for (const req of storeMediaRequirements) {
            if (!map.has(req.storeId)) {
                map.set(req.storeId, new Set());
            }
            map.get(req.storeId)!.add(req.mediaFormatId);
        }
        console.log('[StoreFormatGroupEditor] Built storeFormatMap with', map.size, 'stores');
        return map;
    }, [storeMediaRequirements]);

    // Build POPAd availability map for quick lookup
    const popadAvailabilityMap = useMemo(() => {
        const map = new Map<string, PopadAvailability>();
        if (popadAvailabilityData?.availability) {
            for (const avail of popadAvailabilityData.availability) {
                const key = `${avail.storeId}-${avail.mediaFormatId}`;
                map.set(key, avail);
            }
        }
        return map;
    }, [popadAvailabilityData]);

    // Server-side validation
    // NOTE: Per spec, validation runs ONLY on explicit "Save Group" or "Save Campaign" actions
    // No validation errors appear until user explicitly requests validation
    const validateMutation = useMutation({
        mutationFn: async (groupsToValidate: StoreFormatGroup[]) => {
            // Use 0 as placeholder if no campaign ID yet
            const id = campaignId ?? 0;
            const response = await apiFetch(`/api/campaigns/${id}/groups/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groups: groupsToValidate }),
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Validation failed: ${response.status} ${text}`);
            }
            return response.json() as Promise<ValidationResponse>;
        },
        onSuccess: (result) => {
            setValidationResult(result);
            setIsValidating(false);
        },
        onError: (error: Error) => {
            console.error('[GroupEditor] Validation error:', error);
            setIsValidating(false);
            toast({
                title: 'Validation Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });

    // Clear validation when groups change (don't show stale errors)
    useEffect(() => {
        if (groups.length === 0) {
            setValidationResult(null);
        }
    }, [groups]);

    // Manual validation trigger - called on "Save Group" or when user clicks validate
    const validateGroups = useCallback(() => {
        if (groups.length === 0) {
            setValidationResult(null);
            return;
        }
        setIsValidating(true);
        validateMutation.mutate(groups);
    }, [groups, validateMutation]);

    // Debug logging for pack data
    useEffect(() => {
        console.log('[StoreFormatGroupEditor] Available packs:', availablePacks);
        console.log('[StoreFormatGroupEditor] Pack snapshots data:', packSnapshotsData);
    }, [availablePacks, packSnapshotsData]);

    // Filter packs based on search query
    const filteredPacks = useMemo(() => {
        if (!packSearchQuery.trim()) return availablePacks;
        const query = packSearchQuery.toLowerCase();
        return availablePacks.filter(pack => 
            pack.name?.toLowerCase().includes(query) ||
            pack.description?.toLowerCase().includes(query)
        );
    }, [availablePacks, packSearchQuery]);

    // Check if a pack has a locked snapshot available
    const hasPackSnapshot = useCallback((packId: number): boolean => {
        const snapshotData = packSnapshotsData[packId];
        console.log(`[hasPackSnapshot] packId=${packId}, snapshotData=`, snapshotData);
        return !!snapshotData?.snapshot;
    }, [packSnapshotsData]);

    // CRUD operations
    const openAddGroupDialog = useCallback(() => {
        setSelectedStartMode('scratch');
        setShowStartModeDialog(true);
    }, []);

    const handleStartModeConfirm = useCallback(() => {
        setShowStartModeDialog(false);
        
        if (selectedStartMode === 'scratch') {
            // Create empty group
            const newGroup: StoreFormatGroup = {
                groupId: generateGroupId(),
                groupName: `Group ${groups.length + 1}`,
                storeIds: [],
                formatEntries: [],
                labelId: null,
            };
            const newGroups = [...groups, newGroup];
            onGroupsChange(newGroups);
            setExpandedGroups(prev => new Set(prev).add(newGroup.groupId));
        } else {
            // Open pack selection dialog
            setSelectedPackId(null);
            setPackSearchQuery('');
            setShowPackSelectionDialog(true);
        }
    }, [selectedStartMode, groups, onGroupsChange]);

    const handlePackSelectionConfirm = useCallback(() => {
        if (!selectedPackId) return;
        
        const pack = availablePacks.find(p => p.id === selectedPackId);
        if (!pack) {
            toast({
                title: 'Error',
                description: 'Selected pack not found',
                variant: 'destructive',
            });
            return;
        }

        const snapshotResponse = packSnapshotsData[selectedPackId];
        console.log('[handlePackSelectionConfirm] Pack snapshot response for packId', selectedPackId, ':', snapshotResponse);
        console.log('[handlePackSelectionConfirm] Response keys:', snapshotResponse ? Object.keys(snapshotResponse) : 'null');
        
        if (!snapshotResponse?.snapshot) {
            console.error('[handlePackSelectionConfirm] No snapshot found in response:', snapshotResponse);
            toast({
                title: 'Pack Not Ready',
                description: 'This pack does not have a locked snapshot. Please select a different pack or create from scratch.',
                variant: 'destructive',
            });
            return;
        }

        const snapshot = snapshotResponse.snapshot;
        console.log('[handlePackSelectionConfirm] Snapshot keys:', Object.keys(snapshot));
        console.log('[handlePackSelectionConfirm] Snapshot stores:', snapshot.stores);
        console.log('[handlePackSelectionConfirm] Snapshot templates:', snapshot.templates);
        console.log('[handlePackSelectionConfirm] Full snapshot:', JSON.stringify(snapshot, null, 2));

        // Convert pack snapshot to group (deep copy)
        const newGroup = convertPackToGroup(
            selectedPackId,
            pack.name || `Pack ${selectedPackId}`,
            snapshot,
            groups.length + 1
        );

        const newGroups = [...groups, newGroup];
        onGroupsChange(newGroups);
        setExpandedGroups(prev => new Set(prev).add(newGroup.groupId));
        setShowPackSelectionDialog(false);

        toast({
            title: 'Group Created from Pack',
            description: `"${newGroup.groupName}" has been created with ${newGroup.storeIds.length} stores and ${newGroup.formatEntries.length} formats. You can edit all fields.`,
        });
    }, [selectedPackId, availablePacks, packSnapshotsData, groups, onGroupsChange, toast]);

    // Legacy addGroup function (for backward compatibility)
    const addGroup = useCallback(() => {
        // If packs are available, show the start mode dialog
        if (availablePacks.length > 0) {
            openAddGroupDialog();
        } else {
            // No packs available, create empty group directly
            const newGroup: StoreFormatGroup = {
                groupId: generateGroupId(),
                groupName: `Group ${groups.length + 1}`,
                storeIds: [],
                formatEntries: [],
                labelId: null,
            };
            const newGroups = [...groups, newGroup];
            onGroupsChange(newGroups);
            setExpandedGroups(prev => new Set(prev).add(newGroup.groupId));
        }
    }, [groups, onGroupsChange, availablePacks, openAddGroupDialog]);

    const updateGroup = useCallback((groupId: string, updated: StoreFormatGroup) => {
        onGroupsChange(groups.map(g => g.groupId === groupId ? updated : g));
    }, [groups, onGroupsChange]);

    const deleteGroup = useCallback((groupId: string) => {
        onGroupsChange(groups.filter(g => g.groupId !== groupId));
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.delete(groupId);
            return next;
        });
    }, [groups, onGroupsChange]);

    const moveFormatsBetweenGroups = useCallback((args: {
        sourceGroupId: string;
        selectedIndexes: number[];
        targetGroupId: string | null;
        newGroupName: string;
    }) => {
        const sourceGroup = groups.find((group) => group.groupId === args.sourceGroupId);
        if (!sourceGroup) return;

        const selectedIndexSet = new Set(args.selectedIndexes);
        const movingEntries = sourceGroup.formatEntries
            .filter((_, idx) => selectedIndexSet.has(idx))
            .map((entry) => ({ ...entry }));

        if (movingEntries.length === 0) return;
        if (movingEntries.length >= sourceGroup.formatEntries.length) {
            toast({
                title: 'Cannot move all formats',
                description: 'Leave at least one format in the source group. Delete the group separately if it is no longer needed.',
                variant: 'destructive',
            });
            return;
        }

        const selectedIncludesLimitedFormat = movingEntries.some((entry) => {
            const format = mediaFormats.find((candidate) => candidate.id === entry.mediaFormatId);
            return entry.coveragePercent != null || (format && (format as any).capacityType === 'limited');
        });
        if (selectedIncludesLimitedFormat) {
            toast({
                title: 'Cannot move limited-capacity format',
                description: 'Limited-capacity formats must stay in their own group. Move the other formats out instead.',
                variant: 'destructive',
            });
            return;
        }

        const movingFormatIds = new Set(movingEntries.map((entry) => entry.mediaFormatId));
        const sourceAssignmentsToMove = (sourceGroup.operatorAssignments ?? [])
            .filter((assignment) => movingFormatIds.has(assignment.mediaFormatId));

        const updatedSourceGroup: StoreFormatGroup = {
            ...sourceGroup,
            formatEntries: sourceGroup.formatEntries.filter((_, idx) => !selectedIndexSet.has(idx)),
            operatorAssignments: (sourceGroup.operatorAssignments ?? []).filter(
                (assignment) => !movingFormatIds.has(assignment.mediaFormatId),
            ),
        };

        if (args.targetGroupId) {
            const targetGroup = groups.find((group) => group.groupId === args.targetGroupId);
            if (!targetGroup) return;

            const targetIsPopadGroup = targetGroup.formatEntries.some((entry) => {
                const format = mediaFormats.find((candidate) => candidate.id === entry.mediaFormatId);
                return entry.coveragePercent != null || (format && (format as any).capacityType === 'limited');
            });
            if (targetIsPopadGroup) {
                toast({
                    title: 'Cannot move into POPAd group',
                    description: 'POPAd groups must stay on their own. Choose a different group or create a new one.',
                    variant: 'destructive',
                });
                return;
            }

            const targetFormatIds = new Set(targetGroup.formatEntries.map((entry) => entry.mediaFormatId));
            const duplicateFormat = movingEntries.find((entry) => targetFormatIds.has(entry.mediaFormatId));
            if (duplicateFormat) {
                const formatName = mediaFormats.find((format) => format.id === duplicateFormat.mediaFormatId)?.name;
                toast({
                    title: 'Format already in target group',
                    description: `${formatName ?? `Format ${duplicateFormat.mediaFormatId}`} is already in "${targetGroup.groupName}".`,
                    variant: 'destructive',
                });
                return;
            }

            const targetStoreIds = new Set(targetGroup.storeIds);
            const assignmentsForTarget = sourceAssignmentsToMove.filter((assignment) => targetStoreIds.has(assignment.storeId));
            const existingAssignmentKeys = new Set(
                (targetGroup.operatorAssignments ?? []).map((assignment) => `${assignment.storeId}|${assignment.mediaFormatId}`),
            );
            const mergedAssignments = [
                ...(targetGroup.operatorAssignments ?? []),
                ...assignmentsForTarget.filter((assignment) => {
                    const key = `${assignment.storeId}|${assignment.mediaFormatId}`;
                    if (existingAssignmentKeys.has(key)) return false;
                    existingAssignmentKeys.add(key);
                    return true;
                }),
            ];

            const updatedTargetGroup: StoreFormatGroup = {
                ...targetGroup,
                formatEntries: [...targetGroup.formatEntries, ...movingEntries],
                operatorAssignments: mergedAssignments,
            };

            onGroupsChange(groups.map((group) => {
                if (group.groupId === updatedSourceGroup.groupId) return updatedSourceGroup;
                if (group.groupId === updatedTargetGroup.groupId) return updatedTargetGroup;
                return group;
            }));
            setExpandedGroups(prev => new Set(prev).add(updatedTargetGroup.groupId));
            return;
        }

        const newGroup: StoreFormatGroup = {
            groupId: generateGroupId(),
            groupName: args.newGroupName.trim() || `${sourceGroup.groupName || 'Group'} split`,
            storeIds: [...sourceGroup.storeIds],
            formatEntries: movingEntries,
            operatorTaskId: sourceGroup.operatorTaskId ?? null,
            labelId: sourceGroup.labelId ?? null,
            operatorAssignments: sourceAssignmentsToMove,
        };

        onGroupsChange(groups.map((group) => group.groupId === updatedSourceGroup.groupId ? updatedSourceGroup : group).concat(newGroup));
        setExpandedGroups(prev => new Set(prev).add(newGroup.groupId));
    }, [groups, mediaFormats, onGroupsChange, toast]);

    const toggleExpand = useCallback((groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    }, []);

    // Calculate total preview
    const totalPreview = useMemo((): TotalPreviewStats | null => {
        if (!validationResult) return null;
        return validationResult.preview;
    }, [validationResult]);

    const isLoading = storesLoading || formatsLoading || operatorTasksLoading;
    const hasErrors = validationResult?.errors && validationResult.errors.length > 0;
    const hasWarnings = validationResult?.warnings && validationResult.warnings.length > 0;

    return (
        <div className="space-y-4">
            {/* Header with draft notice */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">Store + Format Groups</h3>
                    <p className="text-sm text-muted-foreground">
                        Create groups of stores and media formats.
                    </p>
                </div>

                {!readOnly && (
                    <Button type="button" onClick={addGroup} disabled={isLoading}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Group
                    </Button>
                )}
            </div>

            {/* Draft warning banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800">
                    <strong>Draft groups are not saved until you confirm the campaign.</strong>{' '}
                    Navigating away will discard unsaved changes.
                </p>
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading stores and formats...</span>
                </div>
            )}

            {/* Empty state */}
            {!isLoading && groups.length === 0 && (
                <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <StoreIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground text-center mb-4">
                            No groups created yet. Add a group to get started.
                        </p>
                        {!readOnly && (
                            <Button type="button" onClick={addGroup}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Your First Group
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Group cards */}
            {!isLoading && groups.length > 0 && (
                <div className="space-y-3">
                    {groups.map((group, index) => (
                        <GroupCard
                            key={group.groupId}
                            group={group}
                            index={index}
                            allGroups={groups}
                            stores={stores}
                            mediaFormats={mediaFormats}
                            operatorTasks={operatorTasks}
                            popadAvailability={popadAvailabilityMap}
                            storeFormatMap={storeFormatMap}
                            errors={validationResult?.errors || []}
                            warnings={validationResult?.warnings || []}
                            retailerLabels={retailerLabels}
                            initialLabelId={initialLabelIds?.get(group.groupId)}
                            onUpdate={(updated) => updateGroup(group.groupId, updated)}
                            onDelete={() => deleteGroup(group.groupId)}
                            onMoveFormats={moveFormatsBetweenGroups}
                            isExpanded={expandedGroups.has(group.groupId)}
                            onToggleExpand={() => toggleExpand(group.groupId)}
                            readOnly={readOnly}
                        />
                    ))}
                </div>
            )}

            {/* Total summary panel */}
            {!isLoading && groups.length > 0 && (
                <Card className="border-muted">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-4">
                        {/* Total preview stats */}
                        {totalPreview && (
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Groups</p>
                                    <p className="font-medium text-lg">{totalPreview.groupCount}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Unique Stores</p>
                                    <p className="font-medium text-lg">{totalPreview.totalStores}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Unique Formats</p>
                                    <p className="font-medium text-lg">{totalPreview.totalFormats}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Assignments</p>
                                    <p className="font-medium text-lg">{totalPreview.totalAssignments}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Total Units</p>
                                    <p className="font-medium text-lg">{totalPreview.totalUnits}</p>
                                </div>
                            </div>
                        )}

                        {/* Validation section - validation runs on demand per spec */}
                        <div className="flex items-center justify-between pt-2 border-t">
                            <div className="flex items-center gap-2 text-sm">
                                {isValidating || validateMutation.isPending ? (
                                    <>
                                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                        <span className="text-muted-foreground">Validating...</span>
                                    </>
                                ) : validationResult ? (
                                    hasErrors ? (
                                        <>
                                            <AlertCircle className="h-3 w-3 text-red-500" />
                                            <span className="text-red-600">{validationResult?.errors?.length} issue{validationResult?.errors?.length !== 1 ? 's' : ''} to fix</span>
                                        </>
                                    ) : hasWarnings ? (
                                        <>
                                            <AlertTriangle className="h-3 w-3 text-yellow-500" />
                                            <span className="text-yellow-600">Valid with {validationResult?.warnings?.length} warning{validationResult?.warnings?.length !== 1 ? 's' : ''}</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                                            <span className="text-green-600">Groups are valid</span>
                                        </>
                                    )
                                ) : (
                                    <span className="text-muted-foreground">Click "Validate" to check groups</span>
                                )}
                            </div>
                            {!readOnly && (
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={validateGroups}
                                    disabled={isValidating || validateMutation.isPending || groups.length === 0}
                                >
                                    {isValidating || validateMutation.isPending ? (
                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                    )}
                                    Validate
                                </Button>
                            )}
                        </div>

                        {/* Validation errors - shown only after validation */}
                        {validationResult && hasErrors && validationResult?.errors && (
                            <div className="text-xs text-muted-foreground space-y-1 bg-red-50 p-2 rounded border border-red-200">
                                {validationResult.errors.map((err, i) => (
                                    <p key={i} className="flex items-start gap-1">
                                        <span className="text-red-400">•</span>
                                        <span className="text-red-700">{err.message}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Group Start Mode Dialog */}
            <Dialog open={showStartModeDialog} onOpenChange={setShowStartModeDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add New Group</DialogTitle>
                        <DialogDescription>
                            Choose how you want to create this group
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <RadioGroup
                            value={selectedStartMode}
                            onValueChange={(value) => setSelectedStartMode(value as GroupStartMode)}
                            className="space-y-3"
                        >
                            <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <RadioGroupItem value="scratch" id="scratch" className="mt-1" />
                                <Label htmlFor="scratch" className="flex-1 cursor-pointer">
                                    <div className="flex items-center gap-2 font-medium">
                                        <PenLine className="h-4 w-4" />
                                        Start from scratch
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Create an empty group and manually select stores, formats, and quantities
                                    </p>
                                </Label>
                            </div>
                            <div className={`flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer ${availablePacks.length === 0 ? 'opacity-50' : ''}`}>
                                <RadioGroupItem 
                                    value="pack" 
                                    id="pack" 
                                    className="mt-1"
                                    disabled={availablePacks.length === 0}
                                />
                                <Label htmlFor="pack" className="flex-1 cursor-pointer">
                                    <div className="flex items-center gap-2 font-medium">
                                        <Package className="h-4 w-4" />
                                        Populate from pack
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Start with stores, formats, and quantities from a pack template (one-time copy, fully editable)
                                    </p>
                                    {availablePacks.length === 0 && (
                                        <p className="text-xs text-amber-600 mt-1">
                                            No locked packs available for this retailer
                                        </p>
                                    )}
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowStartModeDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleStartModeConfirm}>
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pack Selection Dialog */}
            <Dialog open={showPackSelectionDialog} onOpenChange={setShowPackSelectionDialog}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>Select a Pack</DialogTitle>
                        <DialogDescription>
                            Choose a pack to use as a template. Stores, formats, quantities, and operator task will be copied (fully editable after).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search packs..."
                                value={packSearchQuery}
                                onChange={(e) => setPackSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>

                        {/* Pack List */}
                        <div className="border rounded-lg max-h-80 overflow-y-auto">
                            {filteredPacks.length === 0 ? (
                                <div className="py-8 text-center text-sm text-muted-foreground">
                                    {packSearchQuery ? `No packs match "${packSearchQuery}"` : 'No packs available'}
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {filteredPacks.map((pack) => {
                                        const hasSnapshot = hasPackSnapshot(pack.id);
                                        const snapshotData = packSnapshotsData[pack.id]?.snapshot;
                                        const storeCount = snapshotData?.stores?.length || 0;
                                        const formatCount = snapshotData?.templates?.length || 0;
                                        
                                        return (
                                            <div
                                                key={pack.id}
                                                className={`p-3 cursor-pointer transition-colors ${
                                                    selectedPackId === pack.id
                                                        ? 'bg-primary/10 border-l-2 border-l-primary'
                                                        : hasSnapshot
                                                            ? 'hover:bg-muted/50'
                                                            : 'opacity-50 cursor-not-allowed'
                                                }`}
                                                onClick={() => hasSnapshot && setSelectedPackId(pack.id)}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <h4 className="font-medium">{pack.name}</h4>
                                                            {hasSnapshot ? (
                                                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                                                    Locked
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary" className="text-xs">
                                                                    Unlocked
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {pack.description && (
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                {pack.description}
                                                            </p>
                                                        )}
                                                        {hasSnapshot && (
                                                            <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                                                                <span className="flex items-center gap-1">
                                                                    <StoreIcon className="h-3 w-3" />
                                                                    {storeCount} stores
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <FormatIcon className="h-3 w-3" />
                                                                    {formatCount} formats
                                                                </span>
                                                            </div>
                                                        )}
                                                        {!hasSnapshot && (
                                                            <p className="text-xs text-amber-600 mt-1">
                                                                Pack must be locked to use as template
                                                            </p>
                                                        )}
                                                    </div>
                                                    {selectedPackId === pack.id && (
                                                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPackSelectionDialog(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handlePackSelectionConfirm}
                            disabled={!selectedPackId || !hasPackSnapshot(selectedPackId)}
                        >
                            Create Group from Pack
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default StoreFormatGroupEditor;
